import type { FeaturePurchaseActionStyle, FeaturePurchaseCardModel, FeaturePurchasePort } from "./FeaturePurchasePort";
import type { FeatureOfferPort, FeaturePurchaseIconLoader, FeaturePurchaseOfferDefinition } from "./FeaturePurchaseTypes";
import type { BetSystem } from "../bet/BetSystemTypes";
import type { BalanceStore } from "../balance/BalanceStore";
import type { FeatureModeStore } from "../feature-mode/FeatureModeStore";
import type { InteractionBlockStore } from "../interaction-block/InteractionBlockStore";
import type { MoneyFormatter } from "../money/MoneyFormatter";
import type { LocalizationService } from "../localization/LocalizationService";
import type { SpinCoordinator } from "../spin/SpinCoordinator";
import { multiplyMinorByMultiplier } from "../money/multiplyMinorByMultiplier";
import { resolveOfferMultiplier } from "./offerMultiplier";
import type { BarButtonPort } from "../controls/bar/BarButtonPort";
import type { Log } from "@slot/presentation";

export type { FeaturePurchaseIconLoader } from "./FeaturePurchaseTypes";


export interface FeaturePurchasePresenterDeps {
  betSystem: BetSystem;
  balanceStore: BalanceStore;
  featureModeStore: FeatureModeStore;
  interactionBlockStore: InteractionBlockStore;
  formatter: MoneyFormatter;
  localization: LocalizationService;
  log: Log;
}

export interface FeaturePurchasePresenterInput {
  panel: FeaturePurchasePort;
  /** The entry point on the control bar the presenter shows, hides and enables. */
  toggleButton: BarButtonPort;
  deps: FeaturePurchasePresenterDeps;
  spinCoordinator: SpinCoordinator;
  offers: readonly FeaturePurchaseOfferDefinition[];
  featurePriceMultipliersById: Readonly<Record<string, number>>;
  featureModeMultipliersById: Readonly<Record<string, number>>;
  loadIcon: FeaturePurchaseIconLoader;
  /** The control bar's bet steps (with their cue and the overlay policy); the top bar's buttons call them. */
  betActions: { increase(): void; decrease(): void };
  onBuyOffer?: (offerId: string, currentBetMinor: string) => void;
  onBeforeOpen?: () => void;
}

/**
 * The feature purchase flow: the offer cards priced from the current bet (per-offer multipliers from
 * the operator settings), affordability against the balance, the top bar's bet controls, the confirm
 * step, and the outcome — a bonus buy starts a feature round, a feature mode activates in the store.
 * An unlisted offer has no card: the title prompts it on its own (`promptOffer`, decision 51).
 */
export class FeaturePurchasePresenter implements FeatureOfferPort {
  private readonly panel: FeaturePurchasePort;
  private readonly toggleButton: BarButtonPort;
  private readonly deps: FeaturePurchasePresenterDeps;
  private readonly spinCoordinator: SpinCoordinator;
  private readonly offers: readonly FeaturePurchaseOfferDefinition[];
  /** The offers with a card on the panel (every priced offer but the unlisted ones). */
  private readonly listed: readonly FeaturePurchaseOfferDefinition[];
  private readonly icons = new Map<string, string | null>();
  private open = false;
  /** The panel opened on one offer's confirm (`promptOffer`): its Back closes the panel. */
  private prompting = false;
  private selectedOfferId: string | null = null;
  private unsubscribe: Array<() => void> = [];
  /**
   * True when the operator settings leave no purchasable offer (both multiplier maps empty, or every
   * relevant entry missing / zero); the toggle hides and nothing is wired.
   */
  private readonly disabled: boolean;

  public constructor(private readonly input: FeaturePurchasePresenterInput) {
    this.panel = input.panel;
    this.toggleButton = input.toggleButton;
    this.deps = input.deps;
    this.spinCoordinator = input.spinCoordinator;
    this.offers = input.offers.filter((offer) => this.resolveMultiplier(offer) !== null);
    this.listed = this.offers.filter((offer) => offer.listed !== false);
    this.disabled = this.offers.length === 0;
    if (this.disabled) {
      this.toggleButton.setShown(false);
      return;
    }
    this.panel.bind({
      onBackdrop: () => this.close(),
      onClose: () => this.close(),
      onCardAction: (offerId) => this.openConfirm(offerId),
      onBetPlus: () => input.betActions.increase(),
      onBetMinus: () => input.betActions.decrease(),
      onConfirmBack: () => this.closeConfirm(),
      onConfirmPurchase: () => this.confirmPurchase(),
    });
    this.toggleButton.setOnTap(() => this.toggle());
  }

  public async start(): Promise<void> {
    if (this.disabled) return;
    this.open = false;
    this.selectedOfferId = null;
    this.panel.setConfirmOpen(false);
    this.panel.setOpen(false);
    this.panel.setCards(this.listed.map((offer) => this.cardModel(offer)));
    for (const offer of this.offers) this.loadOfferIcon(offer);
    this.render();
    const rerender = () => this.render();
    this.unsubscribe = [
      this.deps.betSystem.subscribe(rerender),
      this.spinCoordinator.subscribe(rerender),
      this.deps.balanceStore.subscribe(rerender),
      this.deps.featureModeStore.subscribe(rerender),
    ];
  }

  public stop(): void {
    if (this.disabled) return;
    for (const off of this.unsubscribe) off();
    this.unsubscribe = [];
    this.selectedOfferId = null;
    this.panel.setConfirmOpen(false);
    this.panel.setOpen(false);
    this.updateToggleButtonState();
    this.deps.interactionBlockStore.remove("feature_purchase");
  }

  public isOpen(): boolean {
    return this.open;
  }

  public openPanel(): void {
    if (this.deps.featureModeStore.getSnapshot().activeMode) return;
    this.input.onBeforeOpen?.();
    this.open = true;
    this.prompting = false;
    this.selectedOfferId = null;
    this.panel.setConfirmOpen(false);
    this.panel.setOpen(true);
    this.updateToggleButtonState();
    this.deps.interactionBlockStore.add("feature_purchase");
    this.render();
  }

  /**
   * Opens one offer's confirm on its own (decision 51): the panel's cards and its bet bar hide behind it as during any
   * confirm, and its Back closes the panel. An unlisted offer's only way in. Refused (false) for an offer the operator
   * left unpriced or the title never declared, while a feature mode is active, and while the bet cannot change (a round
   * plays, or the bet is locked).
   */
  public promptOffer(offerId: string): boolean {
    if (this.disabled || !this.offerById(offerId)) return false;
    if (this.deps.featureModeStore.getSnapshot().activeMode || !this.spinCoordinator.getSnapshot().canChangeBet) return false;
    this.input.onBeforeOpen?.();
    this.open = true;
    this.prompting = true;
    this.panel.setOpen(true);
    this.updateToggleButtonState();
    this.deps.interactionBlockStore.add("feature_purchase");
    this.openConfirm(offerId);
    this.render();
    return true;
  }

  public close(): void {
    this.open = false;
    this.prompting = false;
    this.selectedOfferId = null;
    this.panel.setConfirmOpen(false);
    this.panel.setOpen(false);
    this.updateToggleButtonState();
    this.deps.interactionBlockStore.remove("feature_purchase");
  }

  public closeIfOpen(): void {
    if (this.open) this.close();
  }

  /** Prices, affordability and the bet controls follow the bet, the balance and the round state. */
  public render(): void {
    if (this.disabled) return;
    this.updateToggleButtonState();
    const currentBetMinor = this.deps.betSystem.getCurrentBetMinor();
    const canChangeBet = this.spinCoordinator.getSnapshot().canChangeBet;
    const balance = BigInt(this.deps.balanceStore.getSnapshot().balanceMinor);
    for (const offer of this.listed) {
      const priceMinor = this.resolveOfferPriceMinor(offer, currentBetMinor);
      this.panel.setCardPrice(offer.id, this.priceText(priceMinor));
      // BigInt compare: amounts above Number.MAX_SAFE_INTEGER must not lose precision (real-money rule).
      this.panel.setCardActionEnabled(offer.id, canChangeBet && balance >= BigInt(priceMinor));
    }
    this.panel.setBetAmount(this.deps.formatter.formatMinor(currentBetMinor));
    this.panel.setBetPlusEnabled(canChangeBet && this.deps.betSystem.canIncrease());
    this.panel.setBetMinusEnabled(canChangeBet && this.deps.betSystem.canDecrease());
    this.renderConfirmIfOpen();
  }

  private toggle(): void {
    if (this.open) this.close();
    else this.openPanel();
  }

  private cardModel(offer: FeaturePurchaseOfferDefinition): FeaturePurchaseCardModel {
    return {
      id: offer.id,
      title: this.offerTitle(offer),
      description: this.offerDescription(offer),
      actionLabel: this.actionLabel(offer),
      style: this.actionStyle(offer),
      iconUrl: this.icons.get(offer.id) ?? null,
    };
  }

  private loadOfferIcon(offer: FeaturePurchaseOfferDefinition): void {
    if (!offer.iconPath) {
      this.icons.set(offer.id, null);
      return;
    }
    // An offer table may name the `/spriteFrame` sub-asset; the asset aliases are the bare path.
    const path = offer.iconPath.endsWith("/spriteFrame") ? offer.iconPath.slice(0, -"/spriteFrame".length) : offer.iconPath;
    void this.input.loadIcon(path).then((url) => {
      this.icons.set(offer.id, url);
      if (!url) this.deps.log(`[FeaturePurchase] failed to load icon "${offer.iconPath}"`);
      this.panel.setCardIcon(offer.id, url);
      this.renderConfirmIfOpen();
    });
  }

  private openConfirm(offerId: string): void {
    if (!this.offerById(offerId)) return;
    this.selectedOfferId = offerId;
    this.renderConfirmIfOpen();
    this.panel.setConfirmOpen(true);
  }

  /** Back to the cards; a prompted offer has none behind it, so its Back closes the panel. */
  private closeConfirm(): void {
    if (this.prompting) {
      this.close();
      return;
    }
    this.selectedOfferId = null;
    this.panel.setConfirmOpen(false);
  }

  private confirmPurchase(): void {
    // The confirm's own rule (renderConfirmIfOpen), not only the button's disabled state: nothing is bought or armed while the bet cannot change (review 2026-09-27 #63).
    if (!this.spinCoordinator.getSnapshot().canChangeBet) return;
    const offer = this.selectedOfferId ? this.offerById(this.selectedOfferId) : undefined;
    if (!offer) return;
    const currentBetMinor = this.deps.betSystem.getCurrentBetMinor();
    this.close();
    if (offer.actionType === "bonus_buy") {
      this.input.onBuyOffer?.(offer.id, currentBetMinor);
      return;
    }
    this.deps.featureModeStore.setActiveMode({ id: offer.id, titleKey: offer.titleKey, fallbackTitle: offer.fallbackTitle ?? offer.id });
  }

  private renderConfirmIfOpen(): void {
    const offer = this.selectedOfferId ? this.offerById(this.selectedOfferId) : undefined;
    if (!offer) return;
    const priceMinor = this.resolveOfferPriceMinor(offer, this.deps.betSystem.getCurrentBetMinor());
    const affordable = BigInt(this.deps.balanceStore.getSnapshot().balanceMinor) >= BigInt(priceMinor);
    this.panel.setConfirm({
      title: this.offerTitle(offer),
      description: this.offerDescription(offer),
      amountText: this.priceText(priceMinor),
      actionLabel: this.actionLabel(offer),
      style: this.actionStyle(offer),
      iconUrl: this.icons.get(offer.id) ?? null,
    });
    this.panel.setConfirmActionEnabled(this.spinCoordinator.getSnapshot().canChangeBet && affordable);
  }

  /** The operator's multiplier for the offer: `bonus_buy` from the price map, `feature_mode` from the mode map; null = unavailable. */
  private resolveMultiplier(offer: FeaturePurchaseOfferDefinition): number | null {
    return resolveOfferMultiplier(offer, this.input);
  }

  private resolveOfferPriceMinor(offer: FeaturePurchaseOfferDefinition, currentBetMinor: string): string {
    const multiplier = this.resolveMultiplier(offer);
    if (multiplier === null) throw new Error(`FeaturePurchasePresenter: missing multiplier for offer "${offer.id}" actionType="${offer.actionType}"`);
    return multiplyMinorByMultiplier(currentBetMinor, multiplier);
  }

  private priceText(priceMinor: string): string {
    return `${this.localize("feature_purchase.price", "Price")}: ${this.deps.formatter.formatMinor(priceMinor)}`;
  }

  private offerById(offerId: string): FeaturePurchaseOfferDefinition | undefined {
    return this.offers.find((entry) => entry.id === offerId);
  }

  private offerTitle(offer: FeaturePurchaseOfferDefinition): string {
    return this.localize(offer.titleKey, offer.fallbackTitle ?? offer.id);
  }

  private offerDescription(offer: FeaturePurchaseOfferDefinition): string {
    return this.localize(offer.descriptionKey, offer.fallbackDescription ?? "");
  }

  private actionLabel(offer: FeaturePurchaseOfferDefinition): string {
    return offer.actionType === "feature_mode" ? this.localize("feature_purchase.activate", "Activate") : this.localize("feature_purchase.purchase", "Purchase");
  }

  private actionStyle(offer: FeaturePurchaseOfferDefinition): FeaturePurchaseActionStyle {
    return offer.actionType === "feature_mode" ? "activate" : "purchase";
  }

  private localize(key: string, fallback: string): string {
    return key ? this.deps.localization.t(key, fallback) : fallback;
  }

  private updateToggleButtonState(): void {
    const featureModeActive = !!this.deps.featureModeStore.getSnapshot().activeMode;
    const visible = !featureModeActive && !this.open && this.listed.length > 0;
    this.toggleButton.setShown(visible);
    this.toggleButton.setInteractable(visible && !this.deps.betSystem.isLocked());
  }
}
